'use client'

import { useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'

import {
    type WalkMetrics,
    type WalkState,
    walkState,
} from '@/features/landing/walk/walk'

// The viewport height the walk and the camera measure against: the small
// viewport (the svh unit, the browser's bars shown), read from the page's
// probe. innerHeight follows a phone's address bar, and a re-measure
// against it moved every beat's trigger and end on each change of scroll
// direction (spec §3, v1.1). Falls back to innerHeight off the landing.
export function viewportHeight(): number {
    const probe = document.querySelector<HTMLElement>('[data-walk="viewport"]')
    const h = probe?.getBoundingClientRect().height
    // a probe without height (the unit unsupported) is no probe
    return h !== undefined && h > 0 ? h : window.innerHeight
}

function measure(): WalkMetrics | null {
    const page1 = document.querySelector<HTMLElement>('[data-walk="page-1"]')
    if (!page1) return null
    const y = window.scrollY
    const rect = (el: Element) => {
        const r = el.getBoundingClientRect()
        return { top: r.top + y, height: r.height, bottom: r.bottom + y }
    }
    const gaps: WalkMetrics['gaps'] = []
    for (let n = 1; n <= 4; n++) {
        const g = document.querySelector<HTMLElement>(`[data-walk="gap-${n}"]`)
        if (!g) break
        const r = rect(g)
        gaps.push({ top: r.top, height: r.height })
    }
    return { vh: viewportHeight(), page1Bottom: rect(page1).bottom, gaps }
}

// Reads the sheet's geometry and the scroll, rAF-throttled. Under reduced
// motion the walk still advances by beat but every beat is its finished
// still (t = 1) — spec §4.
export function useWalk(): WalkState {
    const reduce = useReducedMotion()
    const [state, setState] = useState<WalkState>({ beat: 1, t: 0 })
    useEffect(() => {
        let metrics = measure()
        let raf = 0
        const update = () => {
            raf = 0
            metrics ??= measure()
            if (!metrics) return
            const s = walkState(window.scrollY, metrics)
            setState((prev) =>
                prev.beat === s.beat && prev.t === s.t ? prev : s
            )
        }
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update)
        }
        const onResize = () => {
            metrics = measure()
            onScroll()
        }
        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onResize)
        // Late layout shifts (fonts, images, later-mounted gaps) move the
        // sheet without a window resize; re-measure when <main> resizes.
        const main = document.querySelector('main')
        const ro = main ? new ResizeObserver(onResize) : null
        if (main) ro?.observe(main)
        return () => {
            ro?.disconnect()
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onResize)
            if (raf) cancelAnimationFrame(raf)
        }
    }, [])
    return reduce && state.beat > 1 ? { beat: state.beat, t: 1 } : state
}
